import compose from '@hypnosphi/recompose/compose';
import Menu from 'apollo-react/components/Menu';
import MenuItem from 'apollo-react/components/MenuItem';
import TextField from 'apollo-react/components/TextField';
import Tooltip from 'apollo-react/components/Tooltip';
import { withStyles } from '@mui/styles';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';



const styles = theme => ({
  // trick to show the toolback only when hovering 
  textEditor: {
    '&:hover .hoverover': {
      opacity: 1,
      pointerEvents: 'inherit',
    },
  },
  toolbar: {
    '& span:first-child': {
      borderLeftColor: 'transparent',
    },
    '& span': {
      paddingLeft: 8,
      paddingRight: 8,
      color: theme.palette.text.secondary,
      cursor: 'pointer',
      padding: '2px 0',
      display: 'inline-block',
      borderLeftStyle: 'solid',
      borderLeftWidth: 1,
      borderLeftColor: theme.palette.grey[400],
    },
    '& span:hover': {
      color: theme.palette.primary[theme.palette.type === 'light' ? 'dark' : 'light'],
    },
    pointerEvents: 'none',
    opacity: 0.5,
    marginLeft: 8,
    userSelect: 'none',
  },
});

const initialState = {
  mouseX: null,
  mouseY: null,
};

export const TextEditor = (props) => {
  const { 
    classes, 
    rows, 
    name, 
    label, 
    tooltip, 
    value, 
    onChange, 
    onFocus, 
    onBlur, 
    toolbar,
    fields, 
    error,
    disabled,
  } = props;

  const { t } = useTranslation();

  const hasFields = !disabled && fields && fields.length > 0;
  const hasToolbar= !disabled && toolbar && toolbar.length > 0;

  const [state, setState] = useState(initialState);

  const insertAtCursor = text => {
    const input = document.querySelector(`textarea[name="${name}"]`);
    if (!input) {
      return;
    }
    const value = input.value;
    const start = input.selectionStart;
    const end = input.selectionEnd;

    const newValue = `${value.slice(0, start)}$(${text})${value.slice(end)}`;

    if (typeof onChange === 'function') {
      onChange({ target: { value: newValue } });
    }
    
    input.value = newValue;
    input.selectionEnd = start + text.length + 3;
    if (start === end) {
      input.selectionStart = input.selectionEnd;
    }
  };

  const handleClick = (e, action) => {
    e.preventDefault();
    if (e.button === 0) {
      action(e);
    }
  };

  const getToolbar = _ => {
    if (hasFields) {
      const bar = { 
        label: t('webhook.toolbarAdd'), 
        tooltip: t('webhook.toolbarAddTooltip'),
        action: ({clientX, clientY}) => setState({ ...state, mouseX: clientX-2, mouseY: clientY-4 }),
      };
  
      if (hasToolbar) {
        return [bar, ...toolbar];
      } else {
        return [bar];
      }
    } else {
      return toolbar;
    }
  }

  const labelControl = !hasToolbar && !hasFields ? label :
    <span>
      {label}
        <span className={`${classes.toolbar} hoverover`}>
          {getToolbar().filter(t => !t.disabled).map(t => (
            t.tooltip 
            ? (
              <Tooltip key={t.label} title={t.tooltip}>
                <span data-testid={t.label} onMouseDown={e => handleClick(e, t.action)}>
                  {t.label}
                </span>
              </Tooltip>  
            )
            : (
              <span data-testid={t.label} key={t.label} onMouseDown={e => handleClick(e, t.action)}>
                {t.label}
              </span>
            )
          ))}
        </span>
    </span>

  return (
    <>
      { hasFields && (
        <Menu
          keepMounted
          open={state.mouseY !== null}
          onClose={() => setState({...state, mouseX: null, mouseY: null})}
          anchorReference='anchorPosition'
          anchorPosition={
            state.mouseY !== null && state.mouseX !== null
              ? { top: state.mouseY, left: state.mouseX }
              : undefined
          }
        >
          {fields.map((field, index) => (
            <MenuItem
              data-testid={`menuitem-${index}`}
              key={field} onClick={() => { 
                insertAtCursor(field);
                setState({...state, mouseX: null, mouseY: null}); 
              }}>
              <span id={field}>{field}</span>
            </MenuItem>
          ))}
        </Menu>
      )}

      <Tooltip title={tooltip}>
        <TextField
          className={classes.textEditor}
          fullWidth
          multiline
          maxRows={rows || 6}
          minRows={rows || 6}
          name={name}
          label={labelControl}
          value={value}
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
          helperText={error}
          error={!!error}
          disabled={disabled}
        />
      </Tooltip>
    </>
  );
};

export default compose(
  withStyles(styles)
)(TextEditor);
